/**
 * *Remove From Register* on the asset card (`equipment-page-asset-card`, R18–R20).
 *
 * A coral confirmation first (card 08's destructive `Modal`), with *Keep* focused. Ruled 2026-10-20
 * (frame 08, note 7): an asset ON the plan is not refused — the confirmation says where it is placed,
 * that it comes off the plan, and what goes with it; the plan is saved without its placements first,
 * then the register row is deleted. Those are the steps *Remove all* runs, through the same
 * `saveBeforeRemoval`, dry-run cascade and discard route, so the two cannot disagree.
 *
 * Two writes, not one transaction. A plan save that fails or cannot be confirmed deletes nothing. A
 * delete that fails after the plan saved leaves the row unplaced and still in the register, and the
 * modal says so in *Remove all*'s words (`discardFailure`).
 *
 * Refused before anything is written, as *Remove all* refuses: a placement whose plan item has no
 * id, and an id shared by more than one item. And refused outright, with no modal, while the plan
 * is not known — an unknown plan never reads as "not placed".
 */
import React, { useCallback, useRef, useState } from 'react';
import { Button, Icon, Modal, Note } from '@repo/design-system';
import type { RemovedFromLayout } from '@repo/front-end-utils';

import { EquipmentModalBody as Body } from './equipmentModalBody';

/** What a discard came to — the page's `discardInBatches` outcome. */
export interface AssetDiscardOutcome {
  deleted: number[];
  alreadyGone: number[];
  failure: unknown;
}

export type AssetPlanSave =
  | { saved: true; pruned: boolean; removed: RemovedFromLayout }
  | { saved: false };

export interface AssetRemovalFailure {
  title: string;
  message: string;
}

/** The page's removal steps, bound to the twin on screen. */
export interface AssetRemoval {
  /** Whether the plan's placements are known for this row: unknown refuses (R19). */
  isPlacementKnown: (assetId: number) => boolean;
  /** The refids of the row's live placements, in plan order. */
  placementsFor: (assetId: number) => readonly string[];
  /** *Ground floor › Cardio*, one line per placement where it can be named. */
  whereFor: (assetId: number) => string[];
  /** What removing `refids` takes with it — a dry run of `removePlacementsFromLayout`. */
  cascadeFor: (refids: ReadonlySet<string>) => RemovedFromLayout;
  /** *Remove all*'s two refusals, worded for one asset; null when the refids are safe to remove. */
  refusalFor: (refids: ReadonlySet<string>) => string | null;
  /** The sentence for a refusal while the plan is unknown (`placementUnknownMessage`). */
  planUnknown: (subject: string) => string;
  /** A current token, fetched FIRST: a sign-in failure after the plan saved would leave it half done. */
  getToken: () => Promise<string>;
  /** `saveBeforeRemoval` with the modal's error as its sink (R20). */
  saveBeforeRemoval: (
    refids: ReadonlySet<string>,
    report: (failure: AssetRemovalFailure) => void,
  ) => Promise<AssetPlanSave>;
  /** The discard route for these ids, through the step's save state, then a register re-read. */
  discard: (token: string, ids: readonly number[]) => Promise<AssetDiscardOutcome>;
  /** *Remove all*'s account of a discard that stopped. */
  describeFailure: (outcome: AssetDiscardOutcome, planSaved: boolean) => AssetRemovalFailure;
  /** Whether the twin the removal started on is still on screen. */
  stillOn: () => boolean;
}

export interface EquipmentAssetRemoveProps {
  rowId: number;
  name: string;
  removal: AssetRemoval;
  /** The card's alert region: the refusal while the plan is unknown. */
  onRefused: (message: string) => void;
  /** Removed (or found already gone): the page's notice, and the card closes. */
  onRemoved: (notice: string) => void;
  /**
   * The button that opens it: the card's *Remove From Register* (default), or a list row's danger
   * `xs` *Remove*, named *Remove {name}* — the summary's *Not in this file* list
   * (`equipment-page-reimport`, R8). The confirmation and its steps are the same either way.
   */
  trigger?: 'card' | 'row';
  /** Disables the opener — a row's while *Keep All* runs. */
  disabled?: boolean;
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/**
 * What goes with it, in *Remove all*'s words (`OnboardingPage`'s `runRemoveAll`) — each part only
 * when non-zero, so the two removals describe the same loss the same way.
 */
export const cascadeLines = (cascade: RemovedFromLayout): string[] => [
  ...(cascade.observers > 0 ? [plural(cascade.observers, 'observer', 'observers')] : []),
  ...(cascade.compositesRemoved > 0
    ? [plural(cascade.compositesRemoved, 'composite measure', 'composite measures')]
    : []),
  ...(cascade.compositesAmended > 0
    ? [
        plural(
          cascade.compositesAmended,
          'composite measure no longer adds that equipment up',
          'composite measures no longer add that equipment up',
        ),
      ]
    : []),
];

const LIVE_TWIN = 'The live twin is unchanged until you press Go Live.';

interface Pending {
  refids: string[];
  where: string[];
  cascade: RemovedFromLayout;
  refusal: string | null;
  changed: boolean;
}

export const EquipmentAssetRemove: React.FC<EquipmentAssetRemoveProps> = ({
  rowId,
  name,
  removal,
  onRefused,
  onRemoved,
  trigger = 'card',
  disabled = false,
}) => {
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<AssetRemovalFailure | null>(null);
  const opener = useRef<HTMLButtonElement>(null);

  const summary = useCallback((): Pending => {
    const refids = [...removal.placementsFor(rowId)];
    const set = new Set(refids);
    return {
      refids,
      where: refids.length > 0 ? removal.whereFor(rowId) : [],
      cascade: removal.cascadeFor(set),
      refusal: refids.length > 0 ? removal.refusalFor(set) : null,
      changed: false,
    };
  }, [removal, rowId]);

  const open = () => {
    if (!removal.isPlacementKnown(rowId)) {
      onRefused(`${removal.planUnknown(name)} ${name} can’t be removed until the plan has loaded.`);
      return;
    }
    setError(null);
    setPending(summary());
  };

  const close = () => {
    if (busy) return;
    setPending(null);
    setError(null);
  };

  const remove = async () => {
    if (!pending || busy || pending.refusal) return;
    // What it takes is read again at the press: the plan can change while the modal is open.
    const now = summary();
    if (
      now.refids.join('|') !== pending.refids.join('|') ||
      cascadeLines(now.cascade).join('|') !== cascadeLines(pending.cascade).join('|') ||
      now.refusal !== pending.refusal
    ) {
      setPending({ ...now, changed: true });
      return;
    }
    setError(null);
    setBusy(true);
    try {
      let token: string;
      try {
        token = await removal.getToken();
      } catch (failure) {
        setError({
          title: 'Nothing was removed.',
          message:
            failure instanceof Error && failure.message
              ? failure.message
              : 'Could not sign the request. Please try again.',
        });
        return;
      }
      if (!removal.stillOn()) return;

      const placed = now.refids.length > 0;
      let pruned = false;
      let cascade = now.cascade;
      if (placed) {
        const saved = await removal.saveBeforeRemoval(new Set(now.refids), setError);
        if (!saved.saved || !removal.stillOn()) return;
        pruned = saved.pruned;
        cascade = saved.removed;
      }

      const outcome = await removal.discard(token, [rowId]);
      if (!removal.stillOn()) return;
      if (outcome.failure !== undefined) {
        setError(removal.describeFailure(outcome, pruned));
        return;
      }
      setPending(null);
      if (outcome.alreadyGone.includes(rowId)) {
        onRemoved(`${name} had already been removed.`);
        return;
      }
      const parts = cascadeLines(cascade);
      onRemoved(
        `Removed ${name} from the ${placed ? 'plan and register' : 'register'}.` +
          (parts.length > 0 ? ` With it: ${parts.join(', ')}.` : '') +
          ` ${LIVE_TWIN}`,
      );
    } finally {
      setBusy(false);
    }
  };

  const placed = (pending?.refids.length ?? 0) > 0;
  const lines = pending ? cascadeLines(pending.cascade) : [];

  return (
    <>
      {trigger === 'row' ? (
        <Button
          ref={opener}
          type="button"
          variant="destructive"
          size="xs"
          aria-label={`Remove ${name}`}
          disabled={disabled}
          onClick={open}
        >
          Remove
        </Button>
      ) : (
        <Button
          ref={opener}
          variant="text"
          tone="destructive"
          iconLeft={<Icon name="trash-can" size="xs" />}
          aria-label={`Remove ${name} from the register`}
          disabled={disabled}
          onClick={open}
        >
          Remove From Register
        </Button>
      )}
      <Modal
        open={pending !== null}
        onClose={close}
        tone="coral"
        icon={<Icon name="trash-can" />}
        title={
          placed
            ? `Remove ${name} from the plan and the register?`
            : `Remove ${name} from the register?`
        }
        dismissible={!busy}
        returnFocusTo={opener}
        actions={
          <>
            <Button variant="secondary" autoFocus disabled={busy} onClick={close}>
              Keep
            </Button>
            <Button
              variant="destructive"
              loading={busy}
              disabled={!!pending?.refusal}
              onClick={() => void remove()}
            >
              Remove
            </Button>
          </>
        }
      >
        {pending && (
          <Body>
            {placed ? (
              <>
                <p>It is placed at:</p>
                <ul aria-label="Placed at">
                  {(pending.where.length > 0 ? pending.where : ['on the plan']).map(where => (
                    <li key={where}>{where}</li>
                  ))}
                </ul>
                <p>It comes off the plan.</p>
                {lines.length > 0 && (
                  <>
                    <p>With it:</p>
                    <ul aria-label="Goes with it">
                      {lines.map(line => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </>
                )}
                <p>{LIVE_TWIN}</p>
              </>
            ) : (
              <p>This deletes it from the twin’s register. {LIVE_TWIN}</p>
            )}
            {pending.changed && (
              <Note
                tone="butter"
                role="alert"
                icon={<Icon name="triangle-exclamation" size="xs" />}
              >
                What this takes has changed since this opened. Check the list, then Remove again.
              </Note>
            )}
            {pending.refusal && (
              <Note tone="error" role="alert" icon={<Icon name="circle-exclamation" size="xs" />}>
                {pending.refusal}
              </Note>
            )}
            {error && (
              <Note tone="error" role="alert" icon={<Icon name="circle-exclamation" size="xs" />}>
                {`${error.title} ${error.message}`}
              </Note>
            )}
          </Body>
        )}
      </Modal>
    </>
  );
};
